import type { BaseLayerMap, PortraitLayer, PortraitMap, ResultPayload } from "@mesketch/engine";

// Klient API v1 (REQ-017 až REQ-025, REQ-031). Vlastnický token jde jen v hlavičce
// `Authorization: Bearer`, nikdy v URL (REQ-019).

export interface DisplayText {
  readonly value: string;
  readonly status: "ai_draft" | "reviewed" | "approved";
}
export type Texts = Readonly<Record<string, DisplayText | undefined>>;

/** Vstup do testu: Skica zdarma, nebo placený (CR-019). */
export type Entry = "free" | "paid";

export interface CatalogItem {
  readonly slug: string;
  readonly kind: "test" | "tool";
  readonly entry: Entry;
  /** Sekce úvodní stránky, odvozuje ji API (CR-019 §2). */
  readonly section: "main" | "side";
  readonly available: boolean;
  /** Nové sezení jde založit (placený test do plateb ne). */
  readonly startable: boolean;
  /** Vrstva společného portrétu aktuální verze (CR-021), jinak `null`. */
  readonly layer: PortraitLayer | null;
  /** Počet kol aktuální verze (CR-024 §1). */
  readonly rounds: number;
  readonly texts: Texts;
}

export interface Intro {
  readonly slug: string;
  readonly rounds: number;
  readonly items: number;
  readonly citation: string;
  readonly locales: readonly string[];
  readonly texts: Texts;
  /** Pauza s odkazem e-mailem je k dispozici (CR-013). */
  readonly pause_email?: boolean;
  /** Na co se test na začátku zeptá (DEC-007: srovnávací skupina). */
  readonly respondentFields?: readonly ("sex" | "age")[];
  readonly entry: Entry;
  /** Nové sezení jde založit (CR-019 §4). */
  readonly startable: boolean;
}

export interface SessionState {
  readonly id: string;
  readonly locale: string;
  readonly origin: "user" | "preview" | "assigned";
  readonly rounds: number;
  readonly plan: readonly (readonly string[])[];
  readonly answered: readonly { itemId: string; value: readonly number[] | null; round: number }[];
  readonly completedRounds: readonly number[];
  readonly final: boolean;
  readonly maxClientSeq: number;
  /** Formulář pauzy s e-mailem (CR-013): jen `user` a se zapnutým e-mailem. */
  readonly pause_email?: boolean;
  readonly respondentFields?: readonly ("sex" | "age")[];
  readonly respondentSex?: "f" | "m" | null;
}

export interface RatingFormat {
  readonly kind: "rating";
  readonly min: number;
  readonly max: number;
  readonly labels_key: string;
}

export interface RoundItems {
  readonly round: number;
  readonly items: readonly { id: string; format: string; order: number }[];
  readonly formats: Readonly<Record<string, RatingFormat | { kind: string } | undefined>>;
  /** Klíč instrukce kola (CR-002), `null` bez instrukce. */
  readonly instructions: string | null;
  readonly texts: Texts;
}

/** Údaje definice pro vykreslení výsledku (API `view`). */
export interface ResultView {
  readonly slug: string;
  readonly rounds: number;
  readonly domains: readonly string[];
  readonly neutral: readonly [number | null, number | null] | null;
  readonly simulations: number | null;
  readonly candidatesShown: number | null;
  readonly noticeKey: string | null;
  readonly provisionalNorms: boolean;
  readonly free: readonly string[];
  readonly paid?: readonly string[];
  /** Škály s nízkou reliabilitou: opatrnější formulace (zadání 6.5). */
  readonly lowReliability?: readonly string[];
  /** Mapa portrétu verze s portrétem zdarma (CR-015 §3), jinak `null`. */
  readonly portraitMap?: PortraitMap | null;
  /** Vrstva společného portrétu (CR-021 §2). */
  readonly portraitLayer?: PortraitLayer | null;
  /** Mapa vrstvy „Jaký jsem?“ (CR-021 §4). */
  readonly portraitBaseMap?: Pick<BaseLayerMap, "amplify" | "params"> | null;
  /** Druh Skici (CR-028 §0), u starších odpovědí chybí (= `classic`). */
  readonly sketchKind?: "bands" | "order" | "classic";
}

/** Data pro náhled výsledku kola bez připojení (NFR-003): ověří je engine při výpočtu. */
export interface OfflineBundle {
  readonly definition: unknown;
  readonly definitionHash: string;
  readonly norms: unknown;
  readonly texts: Texts;
  readonly view: ResultView;
}

export type FeedbackValue = "fits" | "partly" | "doesnt_fit";

/** Sezení identity v přehledu Moje já (REQ-033), bez odpovědí a skóre. */
export interface OverviewSession {
  readonly id: string;
  readonly slug: string;
  readonly locale: string;
  readonly status: "in_progress" | "done";
  readonly rounds_done: number;
  readonly rounds: number;
  readonly started_at: string;
  readonly finished_at: string | null;
  readonly result_id: string | null;
}

export interface LinkedResult {
  readonly payload: ResultPayload;
  readonly owner: boolean;
  readonly locale: string;
  readonly session_locale: string;
  readonly locales: readonly string[];
  readonly texts: Texts;
  readonly view: ResultView;
  readonly feedback: readonly { scale_id: string | null; section_key: string | null; value: FeedbackValue }[];
  /** Seed portrétu (CR-015 §1), bez portrétu `null`. */
  readonly portrait_seed?: number | null;
  /** Sezení výsledku: jen u výsledku identity (karta páru, REQ-034). */
  readonly session_id?: string;
  /** Adresa obrazu výsledku (CR-027 §4), bez vrstvy `null`. */
  readonly picture_url?: string | null;
  /** Portrét jen majiteli (REQ-037): smí ho otevřít a stav reportu. */
  readonly portrait?: PortraitOffer;
}

export type PortraitStatus = "queued" | "generating" | "ready" | "failed";

export interface PortraitOffer {
  readonly available: boolean;
  readonly status: PortraitStatus | null;
}

/** Sekce zmrazeného Portrétu (`mesketch.report_sections/1`, CR-023 §2). */
export interface PortraitSection {
  readonly key: string;
  readonly source: "pretext" | "ai";
  readonly title: string | null;
  readonly text: string;
}

export interface PortraitReport {
  readonly status: PortraitStatus;
  readonly locale: string;
  readonly sections: readonly PortraitSection[] | null;
}

/** Odkud se Portrét čte a zakládá (REQ-037): sezení s vlastnickým tokenem, nebo identita. */
export type PortraitOwner = { readonly kind: "session"; readonly id: string; readonly token: string } | { readonly kind: "identity"; readonly resultId: string };

export interface ResponseBody {
  readonly item_id: string;
  readonly value: readonly number[];
  readonly client_seq: number;
  readonly duration_ms: number | null;
}

// --- Páry (REQ-034, REQ-035)

export type PairState = "waiting" | "declined" | "cancelled" | "expired" | "ready" | "incomparable" | "same_person" | "unavailable" | "revoked";
export type PairCategory = "harmony" | "complement" | "friction" | "shared_risk";

export interface PairSummary {
  readonly id: string;
  readonly side: "a" | "b";
  readonly other_label: string;
  readonly slug: string;
  readonly state: PairState;
  readonly created_at: string;
  readonly expires_at: string;
}

export interface PairSide {
  readonly scales: Readonly<Record<string, { readonly z: number | null }>>;
  readonly view: ResultView;
  readonly portrait_seed: number | null;
  /** Adresa obrazu výsledku strany (CR-027 §4), bez vrstvy `null`. */
  readonly picture_url: string | null;
}

export interface PairPage {
  readonly id: string;
  readonly state: PairState;
  readonly side: "a" | "b";
  readonly other_label: string;
  readonly slug: string;
  readonly retake: { readonly me: boolean; readonly other: boolean; readonly me_anonymous: boolean } | null;
  readonly evaluation: {
    readonly items: readonly { readonly rule_id: string; readonly category: PairCategory; readonly title: string; readonly body: string; readonly tip: string; readonly placeholder?: boolean }[];
    readonly rules_locale: string;
    readonly me: PairSide;
    readonly other: PairSide;
    readonly texts: Texts;
  } | null;
}

export interface LayerPlace {
  readonly scale: string;
  readonly place: number;
  readonly name: string;
}

interface LayerSource {
  readonly slug: string;
  readonly session_id: string;
}

/** Vstupy vrstev společného portrétu osoby (CR-021 §5). */
export interface PersonLayers {
  readonly seed: number | null;
  readonly layers: {
    readonly base?: LayerSource & { readonly params: Readonly<Record<string, number>> };
    readonly inner?: LayerSource & { readonly params: Readonly<Record<string, number>> };
    readonly sky?: LayerSource & { readonly places: readonly LayerPlace[] };
    readonly land?: LayerSource & { readonly places: readonly LayerPlace[] };
  };
  readonly counted: { readonly done: number; readonly total: number; readonly missing: readonly string[]; readonly ghosts: readonly PortraitLayer[] };
  /** Adresa obrazu osoby (CR-027 §4), bez vrstvy `null`. */
  readonly picture_url: string | null;
  /** Sezení Skici (`view_session_id`) není nejnovější výsledek své metodiky (CR-021 §5). */
  readonly view_old?: boolean;
}

/** Chyba API se stavem HTTP a kódem domény. `status` 0 znamená bez připojení. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${status} ${code}`);
    this.name = "ApiError";
  }
  get offline(): boolean {
    return this.status === 0;
  }
}

async function call<T>(method: string, path: string, opts: { token?: string | undefined; body?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (opts.token !== undefined) headers["authorization"] = `Bearer ${opts.token}`;
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method, headers, body: opts.body === undefined ? null : JSON.stringify(opts.body), cache: "no-store" });
  } catch {
    throw new ApiError(0, "offline");
  }
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const code = (data as { error?: { code?: string } } | null)?.error?.code ?? "error";
    throw new ApiError(res.status, code);
  }
  return data as T;
}

const q = (locale: string) => `locale=${encodeURIComponent(locale)}`;

/** Zdroj výsledku pro náhledy placených stylů (CR-017 §3): odkaz s vlastnickým tokenem, identita, náhled. */
export type PortraitSource = { readonly kind: "link"; readonly token: string; readonly owner: string } | { readonly kind: "identity"; readonly resultId: string } | { readonly kind: "preview"; readonly token: string };

/** Náhled portrétu v placeném stylu jako WebP (`null` při chybě, karta ho pak vynechá). */
async function portraitPreview(source: PortraitSource, style: string): Promise<Blob | null> {
  const body = source.kind === "link" ? { style, result_token: source.token } : source.kind === "identity" ? { style, identity_result_id: source.resultId } : { style, preview_token: source.token };
  const headers: Record<string, string> = { "content-type": "application/json", accept: "image/webp" };
  if (source.kind === "link") headers["authorization"] = `Bearer ${source.owner}`;
  try {
    const res = await fetch("/api/v1/portrait/preview", { method: "POST", headers, body: JSON.stringify(body), cache: "no-store", credentials: "same-origin" });
    return res.ok ? await res.blob() : null;
  } catch {
    return null;
  }
}

export const api = {
  portraitPreview,
  catalog: (locale: string) => call<{ methodologies: CatalogItem[] }>("GET", `/catalog?${q(locale)}`),
  intro: (slug: string, locale: string) => call<Intro>("GET", `/methodologies/${encodeURIComponent(slug)}/intro?${q(locale)}`),
  createSession: (slug: string, locale: string) => call<{ session_id: string; token: string; locale: string }>("POST", "/sessions", { body: { slug, locale } }),
  previewSession: (token: string) => call<{ session_id: string; token: string; locale: string; screen: string; from_round: number | null }>("POST", "/preview/sessions", { body: { token } }),
  previewPair: (token: string) => call<PairPage & { locale: string }>("POST", "/preview/pair", { body: { token } }),
  previewResult: (token: string) =>
    call<{ screen: "round_result" | "sketch"; round: number | null; locale: string; payload: ResultPayload; texts: Texts; locales: readonly string[]; view: ResultView; portrait_seed?: number | null; picture_url?: string | null }>("POST", "/preview/result", { body: { token } }),
  state: (id: string, token: string) => call<SessionState>("GET", `/sessions/${id}`, { token }),
  items: (id: string, token: string, round: number) => call<RoundItems>("GET", `/sessions/${id}/items?round=${round}`, { token }),
  offline: (id: string, token: string) => call<OfflineBundle>("GET", `/sessions/${id}/offline`, { token }),
  texts: (id: string, token: string) => call<{ texts: Texts; locales: readonly string[]; view: ResultView }>("GET", `/sessions/${id}/texts`, { token }),
  saveResponses: (id: string, token: string, responses: readonly ResponseBody[]) => call<{ saved: number; stale: number }>("PUT", `/sessions/${id}/responses`, { token, body: { responses } }),
  completeRound: (id: string, token: string, round: number) => call<{ completed: true; payload?: ResultPayload; portrait_seed?: number | null }>("POST", `/sessions/${id}/rounds/${round}/complete`, { token }),
  complete: (id: string, token: string) => call<{ completed: true; result_token?: string | null; payload?: ResultPayload }>("POST", `/sessions/${id}/complete`, { token }),
  result: (link: string, locale: string, token?: string) => call<LinkedResult>("GET", `/results/${encodeURIComponent(link)}?${q(locale)}`, { token }),
  revoke: (link: string, token: string) => call<{ result_token: string }>("POST", `/results/${encodeURIComponent(link)}/revoke`, { token }),
  feedback: (link: string, token: string, body: { scale_id: string | null; section_key: string | null; value: FeedbackValue }) =>
    call<undefined>("POST", `/results/${encodeURIComponent(link)}/feedback`, { token, body }),
  resumeEmail: (id: string, token: string, email: string) => call<{ sent: boolean; dev_link?: string }>("POST", `/sessions/${id}/resume-email`, { token, body: { email } }),
  setRespondent: (id: string, token: string, sex: "f" | "m") => call<undefined>("PUT", `/sessions/${id}/respondent`, { token, body: { sex } }),
  claim: (link: string) => call<{ session_id: string; token: string; slug: string; locale: string }>("POST", "/sessions/claim", { body: { token: link } }),
  // Identita a Moje já (REQ-032, REQ-033): cookie přihlášení posílá prohlížeč sám (jen /api).
  identity: () => call<{ email: string }>("GET", "/identity"),
  identityLogin: (email: string, locale: string, returnPath?: string) =>
    call<{ sent: boolean; dev_link?: string }>("POST", "/identity/login", { body: { email, locale, ...(returnPath === undefined ? {} : { return_path: returnPath }) } }),
  identityClaim: (token: string) => call<{ return_path: string }>("POST", "/identity/claim", { body: { token } }),
  identityLogout: (everywhere: boolean) => call<undefined>("POST", everywhere ? "/identity/logout-all" : "/identity/logout", { body: {} }),
  portraitLayers: (sessions: readonly { id: string; token: string }[], locale: string, viewSessionId: string | null = null) =>
    call<PersonLayers>("POST", "/portrait/layers", { body: { sessions, locale, ...(viewSessionId === null ? {} : { view_session_id: viewSessionId }) } }),
  identityAttach: (sessions: readonly { id: string; token: string }[]) => call<{ attached: number; skipped: number }>("POST", "/identity/sessions", { body: { sessions } }),
  identityDevice: (id: string) => call<{ session_id: string; token: string; slug: string; locale: string }>("POST", `/identity/sessions/${id}/device`, { body: {} }),
  identityOverview: () => call<{ email: string; sessions: readonly OverviewSession[] }>("GET", "/identity/overview"),
  identityResult: (id: string, locale: string) => call<LinkedResult>("GET", `/identity/results/${id}?${q(locale)}`),
  identityFeedback: (id: string, body: { scale_id: string | null; section_key: string | null; value: FeedbackValue }) => call<undefined>("POST", `/identity/results/${id}/feedback`, { body }),
  identityLink: (id: string) => call<{ result_token: string }>("POST", `/identity/results/${id}/link`, { body: {} }),
  // Portrét (REQ-037): sezení s vlastnickým tokenem, nebo cookie identity (POST jen JSON).
  portrait: (o: PortraitOwner) =>
    o.kind === "session" ? call<PortraitReport>("GET", `/sessions/${o.id}/portrait`, { token: o.token }) : call<PortraitReport>("GET", `/identity/results/${o.resultId}/portrait`),
  openPortrait: (o: PortraitOwner) =>
    o.kind === "session" ? call<PortraitReport>("POST", `/sessions/${o.id}/portrait`, { token: o.token }) : call<PortraitReport>("POST", `/identity/results/${o.resultId}/portrait`, { body: {} }),
  // Páry (REQ-034, REQ-035): vlastnictví tokenem sezení v `Authorization`, nebo cookie identity.
  sessionPairs: (id: string, token?: string) => call<{ can_invite: boolean; pairs: readonly PairSummary[] }>("GET", `/sessions/${id}/pairs`, { token }),
  identityPairs: () => call<{ pairs: readonly PairSummary[] }>("GET", "/identity/pairs"),
  createPair: (body: { session_id: string; inviter_label: string; invitee_label: string; invitee_email?: string; locale: string }, token?: string) =>
    call<{ pair_id: string; invite_path: string; expires_at: string; email_sent: boolean }>("POST", "/pairs", { token, body }),
  inviteLookup: (token: string) => call<{ inviter_label: string; methodology: { slug: string }; expires_at: string }>("POST", "/pair-invites/lookup", { body: { token } }),
  inviteAccept: (invite: string, source: "invite_page" | "result_page", session?: { id: string; token: string }) =>
    call<{ pair_id: string }>("POST", "/pair-invites/accept", { token: session?.token, body: { token: invite, source, ...(session === undefined ? {} : { session_id: session.id }) } }),
  inviteDecline: (invite: string, source: "invite_page" | "result_page") => call<undefined>("POST", "/pair-invites/decline", { body: { token: invite, source } }),
  revokePair: (id: string, source: "result_page" | "pair_page" | "me_page", session?: { id: string; token: string }) =>
    call<undefined>("POST", `/pairs/${id}/revoke`, { token: session?.token, body: { source, ...(session === undefined ? {} : { session_id: session.id }) } }),
  pair: (id: string, locale: string, session?: { id: string; token: string }) =>
    call<PairPage>("GET", `/pairs/${id}?${q(locale)}${session === undefined ? "" : `&session_id=${session.id}`}`, { token: session?.token }),
};
